import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { fetchSessionInfo, tick, resetSession } from 'store/sessionReducer';

const TICK_INTERVAL = 1000;

const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
    }
};

const sendBrowserNotification = () => {
    if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Gardener session expiring', {
            body: 'Your session is about to expire. Click to extend it.',
            icon: '/favicon.ico',
            requireInteraction: true,
        }).onclick = () => {
            window.focus();
        };
    }
};

export const useSessionTimer = () => {
    const dispatch = useAppDispatch();
    const userInfo = useAppSelector((state) => state.user.info);
    const showWarning = useAppSelector((state) => state.session.showWarning);
    const remainingSeconds = useAppSelector((state) => state.session.remainingSeconds);
    const tickTimer = useRef<NodeJS.Timeout | null>(null);
    const notified = useRef(false);

    useEffect(() => {
        if (!userInfo) {
            cleanup();
            dispatch(resetSession());
            notified.current = false;
            return;
        }

        // 로그인 시 브라우저 알림 권한 요청
        requestNotificationPermission();

        // 로그인 상태: 초기 표시용 세션 정보 1회 fetch + 표시용 카운트다운 시작.
        // 경고/만료/연장 판정은 서버가 WebSocket으로 push한다 (App.tsx 의 /user/topic/session 구독).
        dispatch(fetchSessionInfo());
        startTickTimer();

        return () => {
            cleanup();
        };
    }, [userInfo]);

    // showWarning이 true로 바뀔 때 브라우저 알림 전송
    useEffect(() => {
        if (showWarning && !notified.current) {
            notified.current = true;
            sendBrowserNotification();
        }
        if (!showWarning) {
            notified.current = false;
        }
    }, [showWarning]);

    // 표시 카운트다운이 0에 도달하면 서버에 1회 확인(판정이 아니라 확인). 놓친 EXPIRED push나
    // 재연결 후 stale 상태를 self-heal 한다: 만료됐으면 401 → 인터셉터 로그아웃, 연장됐으면 resync.
    // 주의: GET /api/session/info 는 서버의 activity filter 에서 제외(/api/session/*)라 만료 기준인
    // GD_LAST_ACTIVITY_TIME 을 갱신하지 않는다 → 이 확인이 세션을 연장(keepalive)하지 않는다.
    useEffect(() => {
        if (userInfo && remainingSeconds === 0) {
            dispatch(fetchSessionInfo());
        }
    }, [remainingSeconds, userInfo]);

    const startTickTimer = () => {
        if (tickTimer.current) clearInterval(tickTimer.current);
        tickTimer.current = setInterval(() => {
            dispatch(tick());
        }, TICK_INTERVAL);
    };

    const cleanup = () => {
        if (tickTimer.current) {
            clearInterval(tickTimer.current);
            tickTimer.current = null;
        }
    };
};
